import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import { fetchData } from '../../helpers/apiUtils';
import { PlxMailingCampaign, PlxCampaignStatus } from '../../helpers/plxMailingData';
import moment from 'moment';

interface CampaignListProps {
  onEdit: (campaignId: number) => void;
  onNew: () => void;
}

const CampaignList: React.FC<CampaignListProps> = ({ onEdit, onNew }) => {
  const [campaigns, setCampaigns] = useState<PlxMailingCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PlxCampaignStatus | ''>('');
  const [rootsOnly, setRootsOnly] = useState<boolean>(false);
  const [hideWelcomeCopies, setHideWelcomeCopies] = useState<boolean>(false);

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = '/api/plx-mailings/campaigns?limit=100';
      if (statusFilter) url += `&status=${statusFilter}`;
      if (rootsOnly) url += '&series_roots_only=1';
      if (hideWelcomeCopies) url += '&hide_welcome_copies=1';

      const response = await fetchData<PlxMailingCampaign[]>(url);
      if (response.success && response.data) {
        setCampaigns(response.data);
      } else {
        setError('Fehler beim Laden der Kampagnen');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagnen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, [statusFilter, rootsOnly, hideWelcomeCopies]);

  const handleDelete = async (campaignId: number) => {
    if (!window.confirm('Kampagne wirklich löschen?')) return;

    try {
      const response = await fetchData(`/api/plx-mailings/campaigns/${campaignId}`, {
        method: 'DELETE',
      });
      if (response.success) {
        loadCampaigns();
      } else {
        alert('Fehler beim Löschen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Löschen');
      console.error(err);
    }
  };

  const getStatusChip = (status: PlxCampaignStatus) => {
    const colors: Record<PlxCampaignStatus, 'default' | 'primary' | 'success' | 'error'> = {
      draft: 'default',
      scheduled: 'primary',
      sent: 'success',
      cancelled: 'error',
    };
    const labels: Record<PlxCampaignStatus, string> = {
      draft: 'Entwurf',
      scheduled: 'Geplant',
      sent: 'Gesendet',
      cancelled: 'Abgebrochen',
    };
    return <Chip size="small" label={labels[status]} color={colors[status]} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as PlxCampaignStatus | '')}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="draft">Entwurf</MenuItem>
              <MenuItem value="scheduled">Geplant</MenuItem>
              <MenuItem value="sent">Gesendet</MenuItem>
              <MenuItem value="cancelled">Abgebrochen</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Nur Originale anzeigen — versteckt Resends (Send #2+) und zeigt nur Series-Roots.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={rootsOnly}
                  onChange={(e) => setRootsOnly(e.target.checked)}
                />
              }
              label="Nur Originale"
            />
          </Tooltip>
          <Tooltip title="Welcome-Copies ausblenden — Kampagnen, die eine Position der Welcome-Serie belegen. Sie gehen nur an neue Abonnenten und nie über die reguläre Planung raus.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideWelcomeCopies}
                  onChange={(e) => setHideWelcomeCopies(e.target.checked)}
                />
              }
              label="Welcome-Copies ausblenden"
            />
          </Tooltip>
        </Box>

        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onNew}>
          Neue Kampagne
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                ID
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Name
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Produkt
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                WKZ
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Concept
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Send
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                E-Mail
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Status
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Versand
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                <Tooltip title="Darf die Kampagne im Newsletter beworben werden? Gesperrt bei Storno, fehlender oder toter Landing-Seite, abgelaufenem „Bewerben bis“ oder Mailtexten, die an ein vergangenes Ereignis gebunden sind (KI-Prüfung).">
                  <span>Bewerbbar</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Erstellt
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  Keine Kampagnen gefunden
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{campaign.product_name || '-'}</Typography>
                    {campaign.product_code && (
                      <Typography variant="caption" color="textSecondary">
                        ({campaign.product_code})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{campaign.wkz_code || '-'}</TableCell>
                  <TableCell>{campaign.concept || '-'}</TableCell>
                  <TableCell>
                    {(() => {
                      const sendNumber = Number(campaign.send_number) || 1;
                      const seriesSize = Number(campaign.series_size) || 1;
                      // Welcome-Copies zuerst: sie gehen NIE über die reguläre
                      // Planung raus, nur an neue Abonnenten. Ohne diese
                      // Kennzeichnung sieht die Zeile aus wie ein normaler
                      // Entwurf und lädt zum versehentlichen Einplanen ein.
                      if (campaign.welcome_position) {
                        return (
                          <Tooltip
                            title={`Belegt Position ${campaign.welcome_position} der Welcome-Serie und geht ausschließlich an neue Abonnenten. Nicht regulär einplanen.${
                              campaign.welcome_ab_test
                                ? ' Läuft als A/B-Test gegen die automatische Auswahl.'
                                : ''
                            }`}
                          >
                            <Chip
                              size="small"
                              label={`Welcome ${campaign.welcome_position}${
                                campaign.welcome_ab_test ? ' · A/B' : ''
                              }`}
                              color="secondary"
                            />
                          </Tooltip>
                        );
                      }
                      // Report-Strecken-Copies tragen KEIN `welcome_position`
                      // (die Zuordnung steht in `welcome_flows`) und fielen
                      // deshalb durch den Zweig darüber. Ohne Kennzeichnung
                      // sehen sie aus wie gewöhnliche Entwürfe — und genau die
                      // löscht man am ehesten weg.
                      if (campaign.welcome_flow_id) {
                        return (
                          <Tooltip
                            title={`Copy der Report-Strecke ${campaign.welcome_flow_id}. Geht ausschließlich an Anmelder dieses Sonderreports, nie über die reguläre Planung. Bearbeitet wird sie im Reiter „Report-Strecken" der Welcome-Kampagne.`}
                          >
                            <Chip
                              size="small"
                              label={`Report-Strecke ${campaign.welcome_flow_id}`}
                              color="secondary"
                              variant="outlined"
                            />
                          </Tooltip>
                        );
                      }
                      if (sendNumber === 1 && seriesSize === 1) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        );
                      }
                      if (sendNumber === 1) {
                        // Series root with children
                        return (
                          <Tooltip title={`Series-Root mit ${seriesSize - 1} Resend(s)`}>
                            <Chip
                              size="small"
                              label={`${seriesSize}× gesendet`}
                              color="primary"
                              variant="outlined"
                              icon={<ReplayIcon />}
                            />
                          </Tooltip>
                        );
                      }
                      // Resend (child of a series)
                      return (
                        <Box display="flex" flexDirection="column" gap={0.3}>
                          <Tooltip title={`Send #${sendNumber} von ${seriesSize} in dieser Series`}>
                            <Chip
                              size="small"
                              label={`#${sendNumber}`}
                              color="info"
                              icon={<ReplayIcon />}
                            />
                          </Tooltip>
                          {campaign.parent_campaign_id && (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              variant="caption"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                onEdit(campaign.parent_campaign_id as number);
                              }}
                              sx={{ textAlign: 'left' }}
                            >
                              → Original
                            </Link>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {campaign.has_email ? (
                      <Chip size="small" label="Vorhanden" color="success" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Fehlt" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(campaign.status)}</TableCell>
                  <TableCell>
                    {(campaign.scheduled_count || 0) > 0 || (campaign.sent_count || 0) > 0 ? (
                      <Box display="flex" gap={0.5}>
                        {(campaign.scheduled_count || 0) > 0 && (
                          <Chip
                            size="small"
                            label={`${campaign.scheduled_count} geplant`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {(campaign.sent_count || 0) > 0 && (
                          <Chip
                            size="small"
                            label={`${campaign.sent_count} gesendet`}
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const b = campaign.bewerbbarkeit;
                      if (!b) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        );
                      }
                      const gruende = b.hindernisse.map((h) => h.text);
                      const hinweise = b.warnungen.map((w) => w.text);
                      const titel = [...gruende, ...hinweise].join(' · ') || 'Alle Prüfungen bestanden';
                      return (
                        <Tooltip title={titel}>
                          <Chip
                            size="small"
                            label={b.status === 'gesperrt' ? 'Gesperrt' : hinweise.length ? 'Bewerbbar*' : 'Bewerbbar'}
                            color={b.status === 'gesperrt' ? 'error' : 'success'}
                            variant={b.status === 'gesperrt' ? 'filled' : 'outlined'}
                          />
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{moment(campaign.created_at).format('DD.MM.YYYY')}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onEdit(campaign.id)} title="Bearbeiten">
                      <EditIcon />
                    </IconButton>
                    {campaign.status === 'draft' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(campaign.id)}
                        title="Löschen"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignList;
